import React, {useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {useLocation} from 'react-router-dom';
import ActionButton from './ActionButton';
import logo from '../projects/emailConfirmation-0424/assets/logo@3x.png';
import '../projects/emailConfirmation-0424/screens/EmailConfirmation.css';
import {useQueryToken} from '../utils/dynamicCodes/hooks';
import Analytics from '../utils/Analytics';
import Environment from '../utils/Environment';
import {PAYWALL_EVENTS, PaywallSlug} from '../analytics/paywallEvents';
import useDocumentTitle from '../hooks/useDocumentTitle';
import {getLinkOutLoadDiagnostics} from '../utils/linkOutSession';

/** Opens Rise when it's installed, the App Store otherwise (same link as the success screens). */
export const OPEN_RISE_URL = 'https://rise-sleep.app.link/KlNmSZAstbb';

export function MissingTokenScreen({paywall}: {paywall: PaywallSlug}) {
  const {t} = useTranslation();
  const {pathname} = useLocation();
  useDocumentTitle('Rise');

  useEffect(() => {
    Analytics.track(PAYWALL_EVENTS.actionFailed, {
      paywall,
      env: Environment.getVar('REACT_APP_ENV') ?? 'test',
      action: 'load',
      reason: 'missing_token',
      path: pathname,
      // Why this load had no identity: what the link-out session and query stores held, and whether the load was
      // treated as a fresh visit. Presence flags and ages only.
      ...getLinkOutLoadDiagnostics(),
    });
  }, [paywall, pathname]);

  return (
    <div className="safe-full-screen w-screen flex flex-col justify-center items-center text-white px-6 text-center">
      <img src={logo} alt={t('{{brand}}')} width={56} height={56} />
      <h1 className="text-2xl font-bold mt-6">{t('missing_token.title')}</h1>
      <p className="text-md mt-3 mb-8 opacity-80">{t('missing_token.body')}</p>
      <div className="w-full max-w-sm">
        <ActionButton
          title={t('missing_token.open_rise')}
          onClick={() => {
            window.location.href = OPEN_RISE_URL;
          }}
        />
      </div>
    </div>
  );
}

/**
 * For paywalls that can't do anything without the person's token: Stripe setup waits for it, so
 * without one the page shows prices and a button that never works. That happens when a page is
 * opened without the link's query (a shared or copied URL, "Open in Safari"), so say so instead.
 */
export function RequireToken({paywall, children}: {paywall: PaywallSlug; children: React.ReactNode}) {
  const token = useQueryToken();
  return token ? <>{children}</> : <MissingTokenScreen paywall={paywall} />;
}
